import { mat4, quat, vec3 } from 'gl-matrix';
import { SpatialQueryGrid } from '../../spatial';
import { GltfAnimation } from '../GltfAnimation';
import { AnimationState, FrameSet } from '../AnimationState';
import { IkChain } from './IkChain';
import { solveTwoBoneIk } from './TwoBoneIkSolver';
import { buildChainOverrides } from './runtimeFk';

export class IkSystem {
  public chains: IkChain[] = [];
  public spatialGrid: SpatialQueryGrid | null = null;

  constructor(
    private animation: GltfAnimation,
    private animationState: AnimationState,
  ) {}

  /**
   * Run IK solvers for all enabled chains. Reads current animation frame,
   * solves IK, and writes results to the provided boneOverrides map.
   */
  update(modelMatrix: mat4, boneOverrides: Map<number, mat4>): void {
    const frameSet = this.animationState.getFrames();

    // Body origin Y — the surface the character is standing on
    const bodyY = modelMatrix[13];

    const locked = this.animationState.renderObject.impulseLockedBones;

    for (const chain of this.chains) {
      if (!chain.enabled || chain.weight <= 0) continue;

      // Skip chains that have bones under impulse control
      if (
        locked?.has(chain.rootIndex) ||
        locked?.has(chain.midIndex) ||
        locked?.has(chain.tipIndex)
      )
        continue;

      const rootFrame = frameSet.frames[chain.rootName];
      const midFrame = frameSet.frames[chain.midName];
      const tipFrame = frameSet.frames[chain.tipName];
      if (!rootFrame || !midFrame || !tipFrame) continue;

      // Get object-local joint positions from current animation frame
      const localRootPos = mat4.getTranslation(vec3.create(), rootFrame.jointMatrix);
      const localMidPos = mat4.getTranslation(vec3.create(), midFrame.jointMatrix);
      const localTipPos = mat4.getTranslation(vec3.create(), tipFrame.jointMatrix);

      // Compute inverse model matrix for world→local transforms
      const invModel = mat4.invert(mat4.create(), modelMatrix)!;

      // Compute max leg reach for clamping
      const legReach = vec3.distance(localRootPos, localTipPos);

      // Compute IK target — may return the original tip position if no adjustment needed
      const localTarget = this.computeTarget(
        chain,
        localTipPos,
        localRootPos,
        invModel,
        modelMatrix,
        bodyY,
        legReach,
      );

      // If target matches the animated position, skip IK and clear overrides
      if (
        Math.abs(localTarget[0] - localTipPos[0]) < 0.001 &&
        Math.abs(localTarget[1] - localTipPos[1]) < 0.001 &&
        Math.abs(localTarget[2] - localTipPos[2]) < 0.001
      ) {
        boneOverrides.delete(chain.rootIndex);
        boneOverrides.delete(chain.midIndex);
        boneOverrides.delete(chain.tipIndex);
        if (chain.childIndex !== undefined) boneOverrides.delete(chain.childIndex);
        continue;
      }

      // Compute pole target in local space
      const localPole = this.computePole(chain, localMidPos, invModel);

      // Solve IK — returns new mid and tip positions (bone lengths preserved)
      const result = solveTwoBoneIk(localRootPos, localMidPos, localTipPos, localTarget, localPole);

      // Build override matrices from new positions
      const overrides = buildChainOverrides(
        rootFrame,
        midFrame,
        tipFrame,
        result.midPos,
        result.tipPos,
      );

      // Blend between original and IK result
      if (chain.weight < 1) {
        blendMatrix(
          overrides.rootMatrix,
          rootFrame.jointMatrix,
          overrides.rootMatrix,
          chain.weight,
        );
        blendMatrix(overrides.midMatrix, midFrame.jointMatrix, overrides.midMatrix, chain.weight);
        blendMatrix(overrides.tipMatrix, tipFrame.jointMatrix, overrides.tipMatrix, chain.weight);
      }

      // Write to bone overrides
      boneOverrides.set(chain.rootIndex, overrides.rootMatrix);
      boneOverrides.set(chain.midIndex, overrides.midMatrix);
      boneOverrides.set(chain.tipIndex, overrides.tipMatrix);

      // Propagate to child bone (e.g., toe) — apply the same delta as the tip
      if (chain.childIndex !== undefined && chain.childName) {
        const childFrame = frameSet.frames[chain.childName];
        if (childFrame) {
          const origChildWorld = childFrame.jointMatrix;
          const origTipPos = mat4.getTranslation(vec3.create(), tipFrame.jointMatrix);
          // Offset from tip to child in original pose
          const childOffset = vec3.sub(
            vec3.create(),
            mat4.getTranslation(vec3.create(), origChildWorld),
            origTipPos,
          );
          // Apply the mid delta rotation to the offset (same as tip)
          const rotatedOffset = vec3.transformQuat(vec3.create(), childOffset, overrides.midDelta);
          const newChildPos = vec3.add(vec3.create(), result.tipPos, rotatedOffset);
          const childMatrix = mat4.clone(origChildWorld);
          childMatrix[12] = newChildPos[0];
          childMatrix[13] = newChildPos[1];
          childMatrix[14] = newChildPos[2];
          boneOverrides.set(chain.childIndex, childMatrix);
        }
      }
    }
  }

  private computeTarget(
    chain: IkChain,
    localTipPos: vec3,
    localRootPos: vec3,
    invModelMatrix: mat4,
    modelMatrix: mat4,
    bodyY: number,
    legReach: number,
  ): vec3 {
    // If chain has an explicit target set, use it (transform to local space)
    if (chain.target[0] !== 0 || chain.target[1] !== 0 || chain.target[2] !== 0) {
      return vec3.transformMat4(vec3.create(), chain.target, invModelMatrix);
    }

    // For foot IK: query volume grid for ground height at foot XZ
    if (this.spatialGrid) {
      const worldTip = vec3.transformMat4(vec3.create(), localTipPos, modelMatrix);

      // Query from well above the foot to catch floors both above and below
      const queryY = Math.max(worldTip[1], bodyY) + 2;
      const floorHeight = this.spatialGrid.getFloorHeightBelowSmooth(
        worldTip[0],
        queryY,
        worldTip[2],
      );
      if (floorHeight !== null) {
        // The body stands on bodyY. The foot's ground may differ (slope, edge).
        // Only adjust by the *difference* between body ground and foot ground,
        // so on flat terrain there's zero correction.
        const groundDelta = floorHeight - bodyY;

        // Skip if delta is negligible (flat ground) or foot ground is way too
        // far below (foot is over a void — don't stretch the leg impossibly)
        if (Math.abs(groundDelta) < 0.05 || groundDelta < -legReach * 0.8) {
          return vec3.clone(localTipPos);
        }

        // Shift the animated foot position by the ground height difference
        const worldTarget = vec3.fromValues(worldTip[0], worldTip[1] + groundDelta, worldTip[2]);
        return vec3.transformMat4(vec3.create(), worldTarget, invModelMatrix);
      }
    }

    // No adjustment needed
    return vec3.clone(localTipPos);
  }

  private computePole(chain: IkChain, localMidPos: vec3, invModelMatrix: mat4): vec3 {
    // If chain has an explicit pole target, transform to local space
    if (chain.poleTarget[0] !== 0 || chain.poleTarget[1] !== 0 || chain.poleTarget[2] !== 0) {
      return vec3.transformMat4(vec3.create(), chain.poleTarget, invModelMatrix);
    }

    // Default: push the knee forward (positive Z in local space)
    const pole = vec3.clone(localMidPos);
    pole[2] += 1;
    return pole;
  }
}

/** Blend between original (a) and IK (b) matrices by weight (0=a, 1=b). */
function blendMatrix(out: mat4, a: mat4, b: mat4, weight: number): void {
  // Linearly interpolate each element. This works well for small angular
  // differences (typical for IK corrections) and preserves scale exactly.
  for (let i = 0; i < 16; i++) {
    out[i] = a[i] + (b[i] - a[i]) * weight;
  }
}
